import { message } from 'antd';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import localStorage, { KeyStorage } from 'helpers/localStorage';
import statusCode from 'http-status-codes';
import queryString from 'query-string';
import { Event, sub } from 'subjects';

export type Data = {
  [key: string]: Data | Data[] | string | number | undefined | boolean | null;
};

export interface IRequest {
  url: string;
  params: Data;
  data: any;
}

export interface IResponse {
  statusCode: number;
  message: string;
  data: any;
}

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 10000,
  paramsSerializer: (params) => queryString.stringify(params)
});

instance.interceptors.request.use((request: AxiosRequestConfig) => {
  return requestHandler(request);
});

instance.interceptors.response.use(
  (response: AxiosResponse) => {
    return successHandler(response);
  },
  (error: AxiosError) => {
    return errorHandler(error);
  }
);

const requestHandler = (request: AxiosRequestConfig) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(`>>> [${request.method}]Request API: ${request.url}`);
    console.log('  + Params:     ', request.params);
    console.log('  + Data:       ', request.data);
  }
  return request;
};

const successHandler = (response: AxiosResponse) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(`<<< Response API: ${response.config.url}`, response.data);
  }
  response.data.statusCode === 401 && sub.next(Event.LOGOUT) && window.location.reload();
  return response.data;
};

const errorHandler = (error: AxiosError) => {
  message.error(error.message);
  throw error;
};

const getHeader = (customHeaders: any = {}): any => {
  const auth = localStorage.getObject(KeyStorage.AUTH);
  if (auth) {
    const accessToken = auth.accessToken;
    if (accessToken) {
      return {
        ...customHeaders,
        token: `bearer ${accessToken}`
      };
    }
  }

  return { ...customHeaders };
};

const axiosClient = {
  get: async <ReqType, ResType extends IResponse>(
    url: string,
    params?: ReqType,
    customHeaders: any = {}
  ): Promise<ResType> => {
    const headers = getHeader(customHeaders);
    return instance.get(url, { params, headers });
  },
  post: async <ReqType, ResType extends IResponse>(
    url: string,
    data?: ReqType,
    customHeaders: any = {}
  ): Promise<ResType> => {
    const headers = getHeader(customHeaders);
    return instance.post(url, data, { headers });
  },
  put: async <ReqType, ResType extends IResponse>(
    url: string,
    data?: ReqType,
    customHeaders: any = {}
  ): Promise<ResType> => {
    const headers = getHeader(customHeaders);
    return instance.put(url, data, { headers });
  },
  delete: async <ReqType, ResType extends IResponse>(
    url: string,
    params?: ReqType,
    customHeaders: any = {}
  ): Promise<ResType> => {
    const headers = getHeader(customHeaders);
    return instance.delete(url, { params, headers });
  },
  statusCode,
  paging: {
    limit: 10,
    page: 1
  },
  dataListDefault: { total: 0, items: [] },
  getHeader
};
export default axiosClient;
